import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IcClose, IcPencil, IcChevronRight, IcCalendar, IcImage } from '../../theme/icons';
import productService from '../../services/productService';
import authService from '../../services/authService';
import useIsMobile from '../../hooks/useIsMobile';

// Money shown with the "DT" sign (matches the rest of the app / receipts).
const formatCurrency = (amount) =>
  `${new Intl.NumberFormat('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(amount || 0)} DT`;

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('fr-FR');
};

// Mobile: day/month with the year stacked underneath, to keep the column narrow.
const StackedDate = ({ value }) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return (
    <span className="inline-block leading-tight">
      {d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
      <span className="block text-[10px] text-gray-500">{d.getFullYear()}</span>
    </span>
  );
};

// The API returns absolute image URLs (e.g. http://kipara.store/media/...).
// Reduce to a same-origin relative path so it loads under whatever scheme the
// page uses (avoids mixed-content blocking on HTTPS) and host mismatches.
const mediaUrl = (img) => {
  if (!img) return null;
  try { return new URL(img).pathname; } catch { return img; }
};

// One summary "case" for the history footer band.
const FootBox = ({ label, value, valueClassName = 'text-gray-900', boxClassName = 'border-gray-200' }) => (
  <div className={`rounded-md bg-white border px-3 py-2 text-center ${boxClassName}`}>
    <div className="text-[11px] font-medium uppercase text-gray-500">{label}</div>
    <div className={`mt-0.5 text-base font-bold ${valueClassName}`}>{value}</div>
  </div>
);

// Sub-modal: "Voir détails" history (kipara3). type = 'sales' | 'purchase'.
const ProductHistoryModal = ({ product, type, onClose }) => {
  const isSales = type === 'sales';
  const title = isSales ? 'Historique de vente' : "Historique d'achat";
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // Mobile rows: the N° Fac(ture) column is hidden to kill the side scroll;
  // tapping the row opens the document instead (sales numero IS the sale id;
  // purchase rows carry facture_id).
  const rowTap = isMobile
    ? (r) => {
        if (isSales) navigate(`/vente/${r.numero}/receipt`);
        else if (r.facture_id) navigate(`/facturation/detail/${r.facture_id}`);
      }
    : null;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const load = useCallback(async (range = {}) => {
    setLoading(true);
    setError(null);
    try {
      const fetcher = isSales ? productService.getSalesHistory : productService.getPurchaseHistory;
      const res = await fetcher(product.id, range);
      setData(res);
    } catch (e) {
      setError(e?.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [isSales, product.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const applyFilter = () => load({ startDate, endDate });
  const resetFilter = () => { setStartDate(''); setEndDate(''); load(); };

  const rows = data?.rows || [];
  const totals = data?.totals || { quantity: 0, total: 0 };

  // Marge réelle après vente (remises comprises), en % du coût.
  const margeStr = totals.marge_pct != null
    ? `${new Intl.NumberFormat('fr-TN', { maximumFractionDigits: 1 }).format(totals.marge_pct)} %`
    : '—';

  // Purchase history only: highlight the most-recent purchase rows whose
  // quantities add up to the current stock on hand. Under FIFO (the oldest
  // units are sold first) the units still in stock come from the latest
  // purchases, which sit at the top of this newest-first table. Walk from the
  // top until the running quantity covers the stock — those batches are the
  // ones "still in stock".
  const inStockRows = useMemo(() => {
    const set = new Set();
    if (isSales) return set;
    let remaining = Number(totals.stock ?? 0);
    for (let i = 0; i < rows.length && remaining > 0; i++) {
      if (rows[i].is_avoir) continue; // retour fournisseur : pas une livraison en rayon
      set.add(i);
      remaining -= Number(rows[i].quantity) || 0;
    }
    return set;
  }, [isSales, rows, totals.stock]);

  return (
    <div
      className="modal-backdrop z-[60] flex items-start justify-center p-4 overflow-y-auto"
      onMouseDown={onClose}
    >
      {/* Same window/size as the detail popup (max-w-3xl). Modale (Brand Kit
          §05) : rayon 8, ombre, entête encre 13/16, ✕ 17px gray-400 */}
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl my-8 overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
        {/* Header — compact: product name only, to leave room for the history card. */}
        <div className="modal-head rounded-t-lg">
          <div className="flex-1 flex justify-between items-center gap-3">
            <h2 className="modal-title uppercase truncate">{product.name}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-2 rounded-full shrink-0" aria-label="Fermer">
              <IcClose className="text-xl" />
            </button>
          </div>
        </div>

        <div className="p-4 md:p-6">
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Panel header */}
            <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-800">{title}</h3>
              <button
                onClick={() => setShowFilter((s) => !s)}
                className="btn-s text-sm py-1.5 px-3"
              >
                <IcCalendar /> Filtre date
              </button>
            </div>

            {/* Legend for the in-stock highlight (purchase history only) */}
            {!isSales && !loading && !error && inStockRows.size > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50/60 border-b border-emerald-100 text-xs text-emerald-700">
                <span className="inline-block w-3 h-3 rounded-sm bg-emerald-500 shrink-0" />
                Lignes en vert : quantités encore en stock ({totals.stock ?? 0}), issues des achats les plus récents.
              </div>
            )}

            {/* Date filter */}
            {showFilter && (
              <div className="flex flex-wrap items-end gap-3 px-4 py-3 bg-white border-b border-gray-200">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Du</label>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    className="inp px-2 py-1 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Au</label>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    className="inp px-2 py-1 text-sm" />
                </div>
                <button onClick={applyFilter} className="btn-p text-sm py-1.5 px-3">
                  Appliquer
                </button>
                <button onClick={resetFilter} className="btn-db text-sm py-1.5 px-3">
                  Réinitialiser
                </button>
              </div>
            )}

            {/* Table — taller on mobile now that the header is compact */}
            <div className="h-[55vh] md:h-[45vh] overflow-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-500">Chargement…</div>
              ) : error ? (
                <div className="p-8 text-center text-red-600">{error}</div>
              ) : rows.length === 0 ? (
                <div className="empty-ki m-4"><p className="empty-ki-title text-base mt-0">Aucun enregistrement.</p></div>
              ) : (
                /* Tableau dense (Composants-2 §6) : en-tête th-ki 8/12px,
                   lignes 13px, montants à droite tabulaires, survol #F0FDF4. */
                <table className="min-w-full text-gray-900 text-xs md:text-sm">
                  <thead className="sticky top-0">
                    <tr>
                      <th className="th-ki px-3 py-2 text-left hidden md:table-cell">{isSales ? 'N° Fac' : 'N° Facture'}</th>
                      <th className="th-ki px-2 md:px-3 py-2 text-left">Date</th>
                      {!isSales && (
                        <th className="th-ki px-2 md:px-3 py-2 text-left">
                          <span className="md:hidden">Fourn.</span>
                          <span className="hidden md:inline">Fournisseur</span>
                        </th>
                      )}
                      <th className="th-ki px-2 md:px-3 py-2 text-right">
                        <span className="md:hidden">Qté</span>
                        <span className="hidden md:inline">Quantité</span>
                      </th>
                      <th className="th-ki px-2 md:px-3 py-2 text-right">
                        {isSales
                          ? <><span className="md:hidden">P.U.</span><span className="hidden md:inline">Prix Unitaire</span></>
                          : <><span className="md:hidden">P.U. HT</span><span className="hidden md:inline">Prix unit HT</span></>}
                      </th>
                      {isSales
                        ? <th className="th-ki px-2 md:px-3 py-2 text-right">
                            <span className="md:hidden">Rem.</span>
                            <span className="hidden md:inline">Remise</span>
                          </th>
                        : <th className="th-ki px-2 md:px-3 py-2 text-right">TVA</th>}
                      <th className="th-ki px-2 md:px-3 py-2 text-right">
                        {isSales
                          ? <><span className="md:hidden">Total</span><span className="hidden md:inline">Total payé</span></>
                          : <><span className="md:hidden">Total</span><span className="hidden md:inline">Prix total TTC</span></>}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((r, i) => {
                      const inStock = inStockRows.has(i);
                      return (
                      <tr
                        key={i}
                        onClick={rowTap ? () => rowTap(r) : undefined}
                        className={`${inStock ? 'bg-green-50 hover:bg-green-100' : 'hover:bg-gray-100'}${rowTap ? ' cursor-pointer active:bg-gray-100' : ''}`}
                      >
                        <td className={`px-3 py-2 hidden md:table-cell tabular-nums ${inStock ? 'md:border-l-4 border-green-600 font-semibold text-green-800' : ''}`}>{r.numero}</td>
                        <td className="px-2 md:px-3 py-2 whitespace-nowrap tabular-nums">
                          <span className="md:hidden"><StackedDate value={r.date} /></span>
                          <span className="hidden md:inline">{formatDate(r.date)}</span>
                        </td>
                        {!isSales && (
                          <td className="px-2 md:px-3 py-2 font-semibold">
                            <div className="max-w-[90px] md:max-w-none truncate">{r.supplier || '-'}</div>
                          </td>
                        )}
                        <td className="px-2 md:px-3 py-2 text-right tabular-nums">
                          {r.quantity}
                          {inStock && <span className="bdg bdg-stock-ok ml-2 align-middle">en stock</span>}
                          {(r.is_avoir || r.is_return) && <span className="bdg bdg-warn ml-2 align-middle">{r.is_avoir ? 'Avoir' : 'Retour'}</span>}
                        </td>
                        <td className="px-2 md:px-3 py-2 text-right tabular-nums">{formatCurrency(isSales ? r.unit_price : r.unit_cost_ht)}</td>
                        <td className="px-2 md:px-3 py-2 text-right tabular-nums">
                          {isSales ? `${r.remise || 0}%` : `${r.tva || 0}%`}
                        </td>
                        <td className="px-2 md:px-3 py-2 text-right font-semibold tabular-nums">{formatCurrency(isSales ? r.total : r.total_ttc)}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Totals band — same layout for both histories so the window is the
                same size. Money carries the DT sign; quantity carries "Qté". */}
            {!loading && !error && (
              isSales ? (
                <>
                  {/* Mobile: 3 equal cards — Qté / Payé+Achats / Bénéfice+Marge */}
                  <div className="grid sm:hidden grid-cols-1 gap-2 px-4 py-3 bg-gray-50 border-t border-gray-200">
                    <FootBox label="Total Qté" value={totals.quantity || 0} />
                    <div className="rounded-md bg-white border border-gray-200 px-3 py-2 grid grid-cols-2 gap-2 text-center">
                      <div>
                        <div className="text-[11px] font-medium uppercase text-gray-500">Total payé</div>
                        <div className="mt-0.5 text-base font-bold text-gray-900">{formatCurrency(totals.total)}</div>
                      </div>
                      <div>
                        <div className="text-[11px] font-medium uppercase text-gray-500">Total achats TTC</div>
                        <div className="mt-0.5 text-base font-bold text-orange-600">{formatCurrency(totals.achats_total_ttc)}</div>
                      </div>
                    </div>
                    <div className="rounded-md bg-white border border-gray-200 px-3 py-2 grid grid-cols-2 gap-2 text-center">
                      <div>
                        <div className="text-[11px] font-medium uppercase text-gray-500">Bénéfice</div>
                        <div className="mt-0.5 text-base font-bold text-green-600">{formatCurrency(totals.profit)}</div>
                      </div>
                      <div>
                        <div className="text-[11px] font-medium uppercase text-gray-500">Marge</div>
                        <div className="mt-0.5 text-base font-bold text-blue-600">{margeStr}</div>
                      </div>
                    </div>
                  </div>
                  {/* Desktop: original 5-box row */}
                  <div className="hidden sm:grid grid-cols-5 gap-2 px-4 py-3 bg-gray-50 border-t border-gray-200">
                    <FootBox label="Total Qté" value={totals.quantity || 0} boxClassName="border-gray-100" />
                    <FootBox label="Total payé" value={formatCurrency(totals.total)} valueClassName="text-green-600" boxClassName="border-green-100" />
                    {/* Coût d'achat TTC des unités vendues : couvertes par factures à leur coût,
                        surplus au prix d'achat TTC de la fiche — Total payé − Total achats = Bénéfice */}
                    <FootBox label="Total achats TTC" value={formatCurrency(totals.achats_total_ttc)} valueClassName="text-primary" boxClassName="border-red-100" />
                    {/* Marge réelle après vente (remises comprises), en % du coût */}
                    <FootBox label="Marge" value={margeStr} valueClassName="text-gray-500" />
                    <FootBox label="Bénéfice" value={formatCurrency(totals.profit)} valueClassName="text-blue-600" boxClassName="border-blue-100" />
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-4 py-3 bg-gray-50 border-t border-gray-200">
                  <FootBox label="Prix d'achat TTC" value={formatCurrency(totals.prix_achat_ttc)} />
                  <FootBox label="Coût unit. TTC" value={formatCurrency(totals.avg_unit_ttc)} />
                  <FootBox label="Stock" value={totals.stock ?? 0} />
                  <FootBox label="Coût total TTC" value={formatCurrency(totals.total_ttc)} />
                </div>
              )
            )}
          </div>
        </div>

        {/* Footer — pied de modale (Brand Kit §05) : 11/16px, filet gray-100 */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="btn-g py-2 px-4 transition-all">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

// Read-only product detail shown as a popup over the list (kipara2).
// Stock / Prix de vente each open a "Voir détails" history popup.
const ProductDetailModal = ({ product, onClose, onEdit }) => {
  const [history, setHistory] = useState(null); // 'sales' | 'purchase' | null
  // Sellers can view a product but not edit it or open its purchase/sales
  // history (financial detail). Manager/admin keep full access.
  const canManage = authService.isManagerOrAdmin();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { if (history) setHistory(null); else onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, history]);

  // Lock background scroll while the popup (detail or its history) is open.
  useEffect(() => {
    if (!product) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [product]);

  if (!product) return null;

  const price = Number(product.price) || 0;
  const remise = Number(product.remise) || 0;
  const priceAfterRemise = remise > 0 ? price * (1 - remise / 100) : price;
  const categoryName = (product.category && product.category.name) || product.category_name;
  const subName = product.subcategory && typeof product.subcategory === 'object'
    ? product.subcategory.name
    : (product.subcategory_name || (typeof product.subcategory === 'string' ? product.subcategory : null));
  const labName = product.laboratoire && typeof product.laboratoire === 'object'
    ? product.laboratoire.name
    : (product.laboratoire_name || (typeof product.laboratoire === 'string' ? product.laboratoire : null));
  const stock = typeof product.stock === 'number' ? product.stock : 0;
  const lowStock = stock <= (product.stock_min || 0);

  // Show only one window at a time: the history popup replaces the detail popup.
  if (history) {
    return <ProductHistoryModal product={product} type={history} onClose={() => setHistory(null)} />;
  }

  return (
    <div
      className="modal-backdrop z-50 flex items-start justify-center p-4 overflow-y-auto"
      onMouseDown={onClose}
    >
      {/* Modale (Brand Kit §05) : rayon 8, ombre, entête encre 13/16, titre
          600 16px, ✕ 17px gray-400, pied 11/16 filet gray-100 */}
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl my-8 overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-head rounded-t-lg p-6">
          <div className="flex-1 flex justify-between items-start gap-3">
            <div className="flex-1 min-w-0">
              {labName && (
                <span className="bdg bdg-outline mb-2">
                  ⚗ {labName}
                </span>
              )}
              <h1 className="modal-title text-xl sm:text-2xl uppercase break-words min-w-0">{product.name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                {subName && <span className="bdg bdg-subcat">{subName}</span>}
                {categoryName && <span className="bdg bdg-cat">{categoryName}</span>}
                {product.code && (
                  <span className="ml-auto text-gray-300 font-medium whitespace-nowrap">
                    {product.code}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-2 rounded-full shrink-0" aria-label="Fermer">
              <IcClose className="text-xl" />
            </button>
          </div>
        </div>

        <div className="p-6 flex flex-col gap-6">
          {/* Description + photo. On mobile this block reads after the Stock /
              Prix cards, description above the image; desktop keeps the
              original layout (description+photo first, side by side). */}
          <div className="border border-gray-200 rounded-lg p-5 flex flex-col md:flex-row gap-5 order-2 md:order-1">
            <div className="flex-1">
              <div className="text-sm font-semibold text-gray-500 uppercase mb-2">Description</div>
              <p className="text-base font-semibold leading-relaxed text-gray-900 whitespace-pre-line">
                {product.description || 'Aucune description.'}
              </p>
            </div>
            <div className="w-full md:w-48 flex-shrink-0 rounded-lg bg-white border border-gray-200 flex items-center justify-center overflow-hidden p-1">
              {product.image ? (
                // Keep each image's own aspect ratio (no cropping); just bound its size.
                <img
                  src={mediaUrl(product.image)}
                  alt={product.name}
                  className="max-h-44 max-w-full w-auto h-auto object-contain"
                />
              ) : (
                <div className="flex flex-col items-center text-gray-400 py-10">
                  <IcImage className="text-3xl mb-1" />
                  <span className="text-xs">photo</span>
                </div>
              )}
            </div>
          </div>

          {/* Stock + Prix de vente cards, each with "Voir détails".
              Mobile: shown first, Stock stacked above Prix de vente. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 order-1 md:order-2">
            <div className={`rounded-lg p-4 border ${lowStock ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
              <div className={`text-xs font-medium uppercase ${lowStock ? 'text-red-700' : 'text-green-700'}`}>Stock</div>
              <div className={`mt-1 text-2xl font-bold flex items-center gap-2 ${lowStock ? 'text-red-900' : 'text-green-900'}`}>
                {stock}
                {lowStock && (Number(stock) <= 0
                  ? <span className="bdg bdg-rupture">Rupture</span>
                  : <span className="bdg bdg-stock-low">{stock} · Stock bas</span>)}
              </div>
              {canManage && (
                <button
                  onClick={() => setHistory('purchase')}
                  className={`mt-2 inline-flex items-center gap-1 text-sm font-medium ${lowStock ? 'text-red-700 hover:text-red-900' : 'text-green-700 hover:text-green-900'}`}
                >
                  Historique d'achat <IcChevronRight className="text-xs" />
                </button>
              )}
            </div>

            <div className={`rounded-lg p-4 border ${lowStock ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
              <div className={`text-xs font-medium uppercase ${lowStock ? 'text-red-700' : 'text-green-700'}`}>Prix de vente</div>
              <div className={`mt-1 text-2xl font-bold flex items-center gap-2 flex-wrap ${lowStock ? 'text-red-900' : 'text-green-900'}`}>
                {remise > 0 ? (
                  <>
                    <span>{formatCurrency(priceAfterRemise)}</span>
                    <span className="text-sm font-medium text-gray-400 line-through">{formatCurrency(price)}</span>
                    <span className="bdg bdg-promo">−{remise}%</span>
                  </>
                ) : (
                  <span>{formatCurrency(price)}</span>
                )}
              </div>
              {canManage && (
                <button
                  onClick={() => setHistory('sales')}
                  className={`mt-2 inline-flex items-center gap-1 text-sm font-medium ${lowStock ? 'text-red-700 hover:text-red-900' : 'text-green-700 hover:text-green-900'}`}
                >
                  Voir détails <IcChevronRight className="text-xs" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Footer actions — only Modifier here; delete lives inside the edit screen.
            Sellers can't edit, so the footer is hidden for them. */}
        {canManage && (
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button onClick={onClose} className="btn-g py-2 px-4 transition-all">Fermer</button>
            <button
              onClick={() => onEdit(product.id)}
              className="btn-g-edit py-2 px-4 transition-all"
            >
              <IcPencil className="mr-2" /> Modifier
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailModal;
